// Voice-to-Strategy: client for the audio recording + transcription API.
// Records can be scoped to the whole SESSION, a RACE, or a HORSE so that
// transcribed notes sort by race and by horse.
import { API_BASE_URL } from '../config';
import { authHeader, getToken } from './auth';

export type VoiceScope = 'session' | 'race' | 'horse';

export type VoiceStatus =
  | 'transcribing'
  | 'transcribed'
  | 'empty'
  | 'failed'
  | 'awaiting_key';

export interface VoiceRecording {
  id: string;
  user_id: string | null;
  scope: VoiceScope;
  session_id: string | null;
  race_id: string | null;
  meeting_name: string | null;
  race_number: number | null;
  date: string | null;
  horse_name: string | null;
  status: VoiceStatus;
  transcript: string | null;
  error: string | null;
  duration_ms: number | null;
  mime_type: string | null;
  timestamp: string;
  // Sharing: true once an admin has approved the note for all users to see.
  approved: boolean;
  approved_at: string | null;
  // Owner's username (present on shared/admin listings).
  username?: string | null;
}

export interface VoiceContext {
  sessionId?: string;
  meetingName?: string;
  raceNumber?: number | string;
  date?: string;
  horseName?: string;
}

const buildParams = (scope: VoiceScope, ctx: VoiceContext): URLSearchParams => {
  const params = new URLSearchParams({ scope });
  if (ctx.sessionId) params.set('session_id', ctx.sessionId);
  if (ctx.meetingName) params.set('meeting_name', ctx.meetingName);
  if (ctx.raceNumber != null && ctx.raceNumber !== '') {
    params.set('race_number', String(ctx.raceNumber));
  }
  if (ctx.date) params.set('date', ctx.date);
  if (ctx.horseName) params.set('horse_name', ctx.horseName);
  return params;
};

export const uploadRecording = async (
  blob: Blob,
  scope: VoiceScope,
  ctx: VoiceContext,
  durationMs?: number
): Promise<VoiceRecording> => {
  const params = buildParams(scope, ctx);
  if (durationMs != null) params.set('duration_ms', String(Math.round(durationMs)));

  const res = await fetch(`${API_BASE_URL}/api/voice/recordings?${params.toString()}`, {
    method: 'POST',
    headers: { 'Content-Type': blob.type || 'audio/webm', ...authHeader() },
    body: blob,
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`Upload failed (${res.status}): ${detail.slice(0, 200)}`);
  }
  return res.json();
};

export const getRecordings = async (
  scope: VoiceScope,
  ctx: VoiceContext
): Promise<VoiceRecording[]> => {
  const params = buildParams(scope, ctx);
  const res = await fetch(`${API_BASE_URL}/api/voice/recordings?${params.toString()}`, {
    headers: { ...authHeader() },
  });
  if (!res.ok) throw new Error(`Failed to load recordings (${res.status})`);
  return res.json();
};

// Authenticated URL for an <audio> element (token passed as a query param).
export const getAudioUrl = (id: string): string =>
  `${API_BASE_URL}/api/voice/recordings/${id}/audio?token=${encodeURIComponent(getToken())}`;

export const retryTranscription = async (id: string): Promise<VoiceRecording> => {
  const res = await fetch(`${API_BASE_URL}/api/voice/recordings/${id}/transcribe`, {
    method: 'POST',
    headers: { ...authHeader() },
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`Retry failed (${res.status}): ${detail.slice(0, 200)}`);
  }
  return res.json();
};

export const deleteRecording = async (id: string): Promise<void> => {
  const res = await fetch(`${API_BASE_URL}/api/voice/recordings/${id}`, {
    method: 'DELETE',
    headers: { ...authHeader() },
  });
  if (!res.ok) throw new Error(`Delete failed (${res.status})`);
};

// Edit a transcript (correct mis-hearings before it's used downstream).
export const updateTranscript = async (
  id: string,
  transcript: string
): Promise<VoiceRecording> => {
  const res = await fetch(`${API_BASE_URL}/api/voice/recordings/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ transcript }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`Save failed (${res.status}): ${detail.slice(0, 200)}`);
  }
  return res.json();
};

// ── Admin: every user's notes ──────────────────────────────────────────────
export interface AdminRecording extends VoiceRecording {
  username: string | null;
}

export const getAllRecordings = async (userId?: string): Promise<AdminRecording[]> => {
  const params = new URLSearchParams();
  if (userId) params.set('user_id', userId);
  const qs = params.toString();
  const res = await fetch(
    `${API_BASE_URL}/api/voice/admin/recordings${qs ? `?${qs}` : ''}`,
    { headers: { ...authHeader() } }
  );
  if (!res.ok) throw new Error(`Failed to load notes (${res.status})`);
  return res.json();
};

// Admin: approve (share with all users) or un-approve a recording.
export const setRecordingApproval = async (
  id: string,
  approved: boolean
): Promise<void> => {
  const res = await fetch(`${API_BASE_URL}/api/voice/admin/recordings/${id}/approval`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ approved }),
  });
  if (!res.ok) throw new Error(`Failed to update approval (${res.status})`);
};

// ── Classifier (transcript → record types) ─────────────────────────────────
export type RecordType =
  | 'PRINCIPLE'
  | 'CONDITIONAL_RULE'
  | 'ENTITY_NOTE'
  | 'RACE_OBSERVATION'
  | 'BET'
  | 'RESULT'
  | 'META';

export interface ClassifiedRecord {
  type: RecordType;
  status: 'committed' | 'proposed';
  text: string;
  entity_name?: string | null;
  tags?: string[];
}

export interface ClassifyResult {
  summary: Record<string, number>;
  records: ClassifiedRecord[];
}

export const classifyRecording = async (id: string): Promise<ClassifyResult> => {
  const res = await fetch(`${API_BASE_URL}/api/voice/recordings/${id}/classify`, {
    method: 'POST',
    headers: { ...authHeader() },
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(detail?.error || `Classify failed (${res.status})`);
  }
  return res.json();
};

// ── Review queue (the confirmation gate, spec §3.1) ────────────────────────
export interface ReviewItem {
  id: string;
  kind: 'rule' | 'entity';
  status: string;
  text: string | null;
  rule_type: RecordType | null;
  parametric_status: string | null;
  entity_type: string | null;
  entity_name: string | null;
  source_recording_id: string | null;
  created_at: string | null;
  // Username of the user who proposed this item (admin queue is cross-user).
  username?: string | null;
}

export const getReviewQueue = async (): Promise<ReviewItem[]> => {
  const res = await fetch(`${API_BASE_URL}/api/voice/review-queue`, { headers: { ...authHeader() } });
  if (!res.ok) throw new Error(`Failed to load review queue (${res.status})`);
  return res.json();
};

export const confirmReview = async (id: string): Promise<void> => {
  const res = await fetch(`${API_BASE_URL}/api/voice/review-queue/${id}/confirm`, {
    method: 'POST',
    headers: { ...authHeader() },
  });
  if (!res.ok) throw new Error(`Confirm failed (${res.status})`);
};

export const rejectReview = async (id: string): Promise<void> => {
  const res = await fetch(`${API_BASE_URL}/api/voice/review-queue/${id}/reject`, {
    method: 'POST',
    headers: { ...authHeader() },
  });
  if (!res.ok) throw new Error(`Reject failed (${res.status})`);
};

// ── Classified notes sorted by race / horse ────────────────────────────────
export interface RaceObservation {
  id: string;
  observation: string;
  tags: string[];
  source_recording_id: string;
  timestamp: string;
  // True when this row comes from another user's admin-approved (shared) note.
  shared?: boolean;
  username?: string | null;
}

export interface EntityNote {
  id: string;
  entity_type: string;
  entity_name: string;
  trait: string;
  resolved: boolean;
  source_recording_id: string;
  timestamp: string;
  shared?: boolean;
  username?: string | null;
}

export const getRaceObservations = async (
  meetingName: string,
  raceNumber: number | string,
  date?: string
): Promise<RaceObservation[]> => {
  const params = new URLSearchParams({ meeting_name: meetingName, race_number: String(raceNumber) });
  if (date) params.set('date', date);
  const res = await fetch(`${API_BASE_URL}/api/voice/race-observations?${params.toString()}`, {
    headers: { ...authHeader() },
  });
  if (!res.ok) throw new Error(`Failed to load observations (${res.status})`);
  return res.json();
};

export const getEntityNotes = async (horseName: string): Promise<EntityNote[]> => {
  const params = new URLSearchParams({ horse_name: horseName });
  const res = await fetch(`${API_BASE_URL}/api/voice/entity-notes?${params.toString()}`, {
    headers: { ...authHeader() },
  });
  if (!res.ok) throw new Error(`Failed to load notes (${res.status})`);
  return res.json();
};

// A stable per-browser session id so "whole session" notes group together.
const SESSION_KEY = 'racing_ui_voice_session';

export const getSessionId = (): string => {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return 'sess_default';
  }
};

export const startNewSession = (): string => {
  const id = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  try {
    localStorage.setItem(SESSION_KEY, id);
  } catch {
    // ignore storage errors
  }
  return id;
};
